import * as _ from 'lodash';
import { sessionEndedMessage } from '../../auth/login/session-ended';
import { SimulationErrorCode } from '../../main/main-content/simulation/api/types';

export const makeSuccessPayload = message => ({
    message
});

export const makeErrorPayload = error => {
    // The server ended the session (RestTemplate has logged the user out): the login page says so, and a toast for each request that
    // was in the air would only say it again, as "API Error: Unauthorized". The toast saga leaves this one out.
    if (error.sessionEnded) {
        return formatError(sessionEndedMessage, undefined, 401, {
            sessionEnded: true
        });
    }
    const { status } = error.response || { status: '' };
    const data = (error.response && error.response.data) || '';
    const firstError = extractFirstError(data);

    // Sometimes the error object may contain multiple errors.
    // In this case, we're only showing the first error.
    if (firstError) {
        const { code, defaultMessage } = firstError;
        return formatError(defaultMessage, code, status);
    } else {
        return formatError(
            wordsOfTheServer(data, status) ||
                wordsForAnErrorWithNoMessage(error),
            data.errorCode,
            status,
            _.pick(data, namedByTheServer)
        );
    }
};

export const sandboxReadOnlyMessage =
    'This is sandbox data and it is read-only: its alerts cannot be dismissed, delegated, given a note or exported.';

/*
  What the server said, in its own words, except for the one refusal that it answers with the same code whatever was tried: a change to
  the data of a sandbox (409 SIMULATION_READ_ONLY). The controls that make one are hidden in a sandbox, so it reaches the user only when
  one slipped through (a row whose sandbox the client does not know), and then it says why in words that fit every such control.
*/
const wordsOfTheServer = (data, status) =>
    status === 409 &&
    data.errorCode === SimulationErrorCode.SIMULATION_READ_ONLY
        ? sandboxReadOnlyMessage
        : data.error;

export const noAnswerMessage =
    'The server is not answering. Try again in a moment.';
export const timeoutMessage =
    'The server took too long to answer. Try again in a moment.';
export const unavailableMessage = status =>
    `The server is not available right now (${status}). Try again in a moment.`;

/*
  What to say when the server gave no message of its own: a 502 or 504 that a proxy answers with an HTML page or nothing (the backend
  restarts on every deploy), or no answer at all (axios: "Network Error", a timeout). Without this the toast read "API Error: undefined".
  An error that is not about a request at all (a bug in this application) keeps its own message, which says more than nothing.
*/
const wordsForAnErrorWithNoMessage = error => {
    const status = error.response && error.response.status;

    if (status === 502 || status === 503 || status === 504) {
        return unavailableMessage(status);
    }
    if (status >= 500) {
        return `The server failed to handle the request (${status}). Try again in a moment.`;
    }
    if (status) {
        return `The server answered ${status} and gave no reason.`;
    }
    if (error.request || error.isAxiosError) {
        return error.code === 'ECONNABORTED' ? timeoutMessage : noAnswerMessage;
    }
    return error.message || 'Something went wrong.';
};

export const permissionDeniedMessage =
    "You don't have permission to change this";

/*
  The error of a change (saving a rule, enabling it, saving risk weights). When the server refuses it for lack of permission, which it
  does with a 403 that has no error code or words of its own, say so in words. A 403 that has an error code (a read-only sandbox, a brand
  that is not allowed) keeps what the server said, which is more telling.
*/
export const makeChangeErrorPayload = error => {
    const payload = makeErrorPayload(error);

    return payload.statusCode === 403 && !payload.errorCode
        ? { ...payload, message: permissionDeniedMessage }
        : payload;
};

export const loginRefusedMessage =
    'Invalid username/password, or the account is not active.';

/*
  The error of a login. A wrong password is refused with an error code and words of the backend's own (409, INVALID_CREDENTIALS,
  "Invalid username/password"), which are kept. An account that is not active (401) and a username that does not exist (400) are refused
  with Spring's bare error, "Unauthorized" or "Bad Request", and nothing else: no code and no words, which tells the user nothing and
  cannot be told apart by the client, so the words say what they have in common.
*/
const bareRefusalsOfALogin = [400, 401];

export const makeLoginErrorPayload = error => {
    const payload = makeErrorPayload(error);

    return _.includes(bareRefusalsOfALogin, payload.statusCode) &&
        !payload.errorCode
        ? { ...payload, message: loginRefusedMessage }
        : payload;
};

const extractFirstError = error => {
    const firstError = error.errors && error.errors[0];
    return firstError || null;
};

/*
  What some errors of the simulation say besides the message and the code, and which the screens use: the run that holds the limit
  (activeRunId of SIMULATION_LIMIT_REACHED), and the run that is not in the state a call needed and the state it is in (runId and
  runStatus of SIMULATION_RUN_STATE and SIMULATION_RUN_NOT_COMPLETED). They are in the payload only when the server wrote them.
*/
const namedByTheServer = ['activeRunId', 'runId', 'runStatus'];

const formatError = (message, errorCode, statusCode, named) => ({
    message,
    errorCode,
    statusCode,
    ...named
});
